import { Checkbox } from '@base-ui/react';
import styled from '@emotion/styled';

import type { CheckboxSize } from './types';

const SIZES: Record<CheckboxSize, { box: number; radius: 4 | 6 }> = {
  sm: { box: 16, radius: 4 },
  md: { box: 20, radius: 6 },
  lg: { box: 24, radius: 6 },
};

const noDollarProp = (prop: string) => !prop.startsWith('$');

export const StyledRoot = styled(Checkbox.Root, { shouldForwardProp: noDollarProp })<{
  $size: CheckboxSize;
}>(({ theme, $size }) => {
  const { box, radius } = SIZES[$size];

  return {
    width: box,
    height: box,
    borderRadius: theme.radius[radius],
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',

    // Appearance — unchecked (default) state
    backgroundColor: theme.colors.background.overlay,
    border: `1px solid ${theme.colors.border.default}`,

    // Behaviour
    cursor: 'pointer',
    outline: 'none',

    // Transitions
    transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',

    // Hover / focus — unchecked only
    '&[data-unchecked]:hover:not([data-disabled])': {
      borderColor: theme.colors.border.strong,
    },
    '&[data-unchecked]:focus-visible:not([data-disabled])': {
      borderColor: theme.colors.border.strong,
    },

    // Focus ring — every state
    '&:focus-visible:not([data-disabled])': {
      boxShadow: `0px 0px 0px 2px ${theme.colors.border.focus}`,
    },

    // Checked / indeterminate share the same fill
    '&[data-checked], &[data-indeterminate]': {
      backgroundColor: theme.colors.brand.solid,
      borderColor: theme.colors.brand.solid,
    },
    '&[data-checked]:hover:not([data-disabled]), &[data-indeterminate]:hover:not([data-disabled])':
      {
        backgroundColor: theme.colors.brand.hover,
        borderColor: theme.colors.brand.hover,
      },
    '&[data-checked]:focus-visible:not([data-disabled]), &[data-indeterminate]:focus-visible:not([data-disabled])':
      {
        backgroundColor: theme.colors.brand.hover,
        borderColor: theme.colors.brand.hover,
      },

    // Disabled state
    '&[data-disabled]': {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  };
});

export const StyledIndicator = styled(Checkbox.Indicator, { shouldForwardProp: noDollarProp })(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.brand.onBrand,
    pointerEvents: 'none',

    '&[data-unchecked]': {
      display: 'none',
    },
  }),
);
